import { type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { SUBSCRIPTION_TERM } from '@cx-sdk/subs/schema';
import { type SubsProduct } from '@cx-web/domain-types';
import { PLUS_REPUB_CONFIG, type SubscriptionProductConfig, type SupportedRepubName } from '@cx-web/repub-config';
import { type RepubIs } from '@cx-web/utils';
import { getCurrencySymbol } from '@cx-web/utils/getCurrencySymbol';

import { type Repub } from '@cx-modules/constants';

import { Button } from '~/components/cxy/cxy.ts';
import { formatPrice } from '~/configuration/utils/formatPrice';
import { useTrackCTAImpression } from '~/hooks/use-track-cta-impression.ts';
import { useTrackGZMetaPixelSubscriptionClick } from '~/pages/gz/hooks/useTrackGZMetaPixelSubscriptionClick';

import styles from './styles/PriceCard.module.css';

export type PriceCardProps = {
  onClick?: () => void;
  product: SubsProduct;
  repub: Repub;
  repubIs: RepubIs;
  subscriptionLinkEnv: 'PROD' | 'NON_PROD';
  userHasCancelledSubscription: boolean;
} & PropsWithChildren;

export function PriceCard({
  onClick,
  product,
  repub,
  repubIs,
  subscriptionLinkEnv,
  userHasCancelledSubscription,
  ...props
}: PriceCardProps) {
  const ref = useTrackCTAImpression({
    price: product.price,
    product: product.term,
    title: 'oddschecker_plus',
    wallType: 'Landing Page',
  });

  const isAnnual = product.term === SUBSCRIPTION_TERM.ANNUAL;
  const isMonthly = product.term === SUBSCRIPTION_TERM.MONTHLY;
  const isGZPrepaidMonthly = repubIs.GZ && isMonthly && product.recurring.count > 1;
  const displayedMonthlyPriceDivisor = isAnnual ? 12 : isGZPrepaidMonthly ? product.recurring.count : 1;
  const monthlyPrice = Math.floor(product.amount / displayedMonthlyPriceDivisor) / 100;
  const isFeaturedPlan = isAnnual || isGZPrepaidMonthly;
  const productPriceParts = formatPrice(product.price);
  const formattedBillingPrice = `${getCurrencySymbol(product.currency)}${productPriceParts.whole}.${productPriceParts.decimal}`;
  const { t } = useTranslation(['label']);

  // Get external URL from config if it exists (GZ only)
  const subscriptionConfig = PLUS_REPUB_CONFIG[repub as SupportedRepubName]?.subscription;
  const products = subscriptionConfig?.products as Record<string, SubscriptionProductConfig> | undefined;
  const productConfig = products?.[product.productId];

  const externalUrl = productConfig?.externalUrl?.[subscriptionLinkEnv];
  const shouldUseExternalLink = Boolean(externalUrl);

  const getButtonText = () => {
    const startTrial = t(isGZPrepaidMonthly ? 'CX_PLUS_START_14_DAY_TRIAL' : 'CX_PLUS_START_7_DAY_TRIAL');
    const subscribeNow = t('CX_SUBSCRIBE_NOW');

    const isGZMonthlyPayAsYouGo = repubIs.GZ && isMonthly && !isGZPrepaidMonthly;

    if (isGZMonthlyPayAsYouGo) return subscribeNow;

    return userHasCancelledSubscription ? subscribeNow : startTrial;
  };

  const buttonText = getButtonText();

  const trackGZMetaPixelClick = useTrackGZMetaPixelSubscriptionClick({
    productId: product.productId,
    externalUrl: repubIs.GZ ? externalUrl : undefined,
    ctaName: buttonText.toLowerCase().replace(/\s+/g, '_'),
  });

  const termText = isGZPrepaidMonthly ? t('CX_GZ_THREE_MONTH_TERM') : isAnnual ? t('CX_ANNUAL') : t('CX_MONTHLY');
  const offerDisclaimerText = isGZPrepaidMonthly ? t('CX_14_DAY_TRIAL') : t('CX_OFFER_DISCLAIMER');
  const paidTermText = isGZPrepaidMonthly
    ? t('CX_GZ_PAID_QUARTERLY')
    : isAnnual
      ? t('CX_PAID_ANNUALLY')
      : t('CX_PAID_MONTHLY');

  return (
    <div
      className={`${styles.priceSelection} ${isFeaturedPlan ? styles.bestValueBorder : ''}`}
      key={product.id}
      ref={ref}
      {...props}
    >
      {isFeaturedPlan && <p className={styles.bestValueTag}>{t('CX_BEST_VALUE')}</p>}
      <p className={styles.term} data-testid="term">
        {termText}
      </p>
      <p className={styles.offerDisclaimer}>{offerDisclaimerText}</p>
      <p className={styles.price} data-testid="price">
        <span className={styles.monthlyCost}>
          {getCurrencySymbol(product.currency)}
          {formatPrice(monthlyPrice).whole}
        </span>
        .{formatPrice(monthlyPrice).decimal} /{t('CX_MONTH')}
      </p>
      <p className={styles.paid} data-testid="paid-term">
        {paidTermText}
        {repubIs.GZ && !isGZPrepaidMonthly && isAnnual && <>&nbsp;({formattedBillingPrice})</>}
      </p>
      {shouldUseExternalLink ? (
        <Button asChild className={styles.subscribeButton} size="large" variant="secondary">
          <a
            href={externalUrl}
            onClick={() => {
              trackGZMetaPixelClick();
              onClick?.();
            }}
            rel="noopener noreferrer"
            target="_blank"
          >
            {buttonText}
          </a>
        </Button>
      ) : (
        <Button className={styles.subscribeButton} onClick={onClick} size="large" variant="secondary">
          {buttonText}
        </Button>
      )}
    </div>
  );
}
